import { CssBaseline } from '@mui/material'
import Router from 'Router'
import Loader from 'SharedComponents/Loader'
import SnackBar from 'SharedComponents/SnackBar'
import { Provider } from 'react-redux'
import Store from 'Redux/Store'
import { ThemeProvider } from '@mui/material/styles'
import { ThemeProvider as ThemeProviderStyle } from '@mui/styles'
import theme from './Theme'
import NetworkService from 'Services/Network'
import { useEffect, useCallback, useMemo, useRef, useState } from "react"
import { useSelector } from "react-redux"
import { LanguageProvider, useLanguage } from "./language"
import ChatBot, {
  ChatBotProvider,
  Button,
  useChatWindow,
  useSettings
} from "react-chatbotify"
import FloatingOrderButton from "./components/FloatingOrderButton"
import supportIcon from './assets/support-icon.png'
import metuaBotAvatarIdle from './assets/metua-bot-idle.svg'
import metuaBotAvatarThinking from './assets/metua-bot-thinking.svg'
import metuaBotAvatarWave from './assets/metua-bot-wave.svg'

// Vrai contenu de la page vitrine "Metua'a Travel Hub" (les 6 services
// de l'écosystème) — importé directement depuis la page elle-même,
// pas recopié à la main : si un·e collègue modifie ce tableau dans la
// page, la réponse de Combi suit automatiquement au prochain build.
import { services as ecosystemServices, featureCards as travelHubFeatureCards } from 'Pages/All/AllMetuaaTravelHub'

// Même principe pour les autres pages produits : on importe leurs
// VRAIS tableaux de contenu (pas de copier-coller à la main), pour
// que les réponses de Combi suivent automatiquement les pages si
// un·e collègue les modifie.
import { featureCards as eatsFeatureCards } from 'Pages/All/AllMetuaaEat'
import { featureCards as mappersFeatureCards } from 'Pages/All/AllMappers'
import { enterpriseFeatureCards } from 'Pages/All/AllEnterprise'
import { driverSteps } from 'Pages/All/AllDriver'
import { stepIntroEditorialContent as mplusStepIntro } from 'Pages/All/MPlusProgram'

// Catalogues de traduction (react-intl) importés bruts : plusieurs de
// ces pages stockent leurs textes sous forme d'identifiants
// (titleId/textId) plutôt qu'en clair. buildFlow n'est pas un
// composant React (donc pas de useIntl() possible ici) — on résout
// ces identifiants nous-mêmes via resolveMessage() plus bas, avec le
// même comportement de repli que react-intl (id renvoyé tel quel si
// la clé n'existe pas).
import frMessages from './language/fr.json'
import enMessages from './language/en.json'

// Mêmes services REST déjà utilisés ailleurs dans l'app (WalletClient,
// ProfileClient...) pour aller chercher les vraies données du compte
// connecté (solde du portefeuille, infos de profil).
import WalletApi from 'Services/Wallet'
import ProfileApi from 'Services/Profile'

// Vrai menu de "l'espace client" connecté (Layouts/DashboardClientLayout) —
// mêmes rubriques que celles affichées dans la sidebar une fois connecté,
// importées directement (pas recopiées à la main) pour rester synchronisées
// si un·e collègue modifie la sidebar.
import { sidebarPrimary as clientSidebarPrimary, sidebarAccount as clientSidebarAccount } from 'Layouts/DashboardClientLayout'

/* =========================================================
   CONFIGURATION
========================================================= */

NetworkService.setupInterceptors(Store)

const minimizeIcon =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='3.5' stroke-linecap='round'%3E%3Cline x1='4' y1='12' x2='20' y2='12'/%3E%3C/svg%3E"


/* =========================================================
   FAQ VOYAGES
   C'est ici que tu entraînes Combi.
========================================================= */

const TRAVEL_FAQ_CATEGORIES = {

  fr: [
    "Voyage",
    "Annulation",
    "Tarifs",
    "Services à bord",
    "Promotions",
    "Paiement",
    "Bagages",
  ],

  en: [
    "Travel",
    "Cancellation",
    "Fares",
    "Onboard Services",
    "Promotions",
    "Payment",
    "Luggage",
  ]

}

const TRAVEL_FAQ = {

  fr: {

  "Voyage": [

    {
      question: "Quand part mon prochain bus ?",

      keywords: [
        "prochain bus",
        "prochain départ",
        "heure départ",
        "heure de départ",
        "bus part",
        "départ"
      ],

      answer:
        "Mon combi, vérifie l'heure de départ sur la page de l'agence que tu as choisie. Si tu veux, je peux aussi t'aider à trouver l'information."
    },

    {
      question: "J'ai raté mon bus, je peux prendre le suivant ?",

      keywords: [
        "raté mon bus",
        "rate mon bus",
        "rater mon bus",
        "bus suivant",
        "prochain bus",
        "prendre le suivant"
      ],

      answer:
        "Si tu as raté ton bus, contacte directement l'agence ou laisse-lui un message WhatsApp pour confirmer si tu peux prendre le prochain départ."
    },

    {
      question: "Je peux payer mon retour maintenant ?",

      keywords: [
        "payer mon retour",
        "payer retour",
        "retour maintenant",
        "billet retour"
      ],

      answer:
        "Oui mon combi ! Tu peux payer ton retour en même temps et bénéficier d'une réduction sur l'opération."
    },

    {
      question: "Quand dois-je arriver avant mon départ ?",

      keywords: [
        "combien de temps avant",
        "temps avant départ",
        "arriver avant",
        "arriver avant départ",
        "quand arriver",
        "30 minutes"
      ],

      answer:
        "Je te conseille d'arriver 30 minutes avant le départ pour effectuer tranquillement les formalités d'embarquement."
    },

    {
      question: "Que se passe-t-il si mon bus tombe en panne ?",

      keywords: [
        "bus tombe en panne",
        "bus en panne",
        "panne bus",
        "tombe en panne",
        "bus panne"
      ],

      answer:
        "Pas de panique mon combi ! La plateforme transmet ta localisation à l'agence, qui peut alors t'envoyer un autre bus pour poursuivre ton trajet."
    }

  ],

  "Annulation": [

    {
      question: "Je peux être remboursé si j'annule mon voyage ?",

      keywords: [
        "remboursé",
        "remboursement",
        "annuler voyage",
        "annulation",
        "annule mon voyage"
      ],

      answer:
        "Tu peux avoir la possibilité de reporter ton voyage ou de voyager à une date ultérieure. Pour connaître les conditions exactes, contacte l'agence concernée."
    }

  ],

  "Tarifs": [

    {
      question: "Quel tarif pour mon enfant ?",

      keywords: [
        "tarif enfant",
        "prix enfant",
        "enfant",
        "tarif pour mon enfant"
      ],

      answer:
        "Le tarif enfant est disponible uniquement en VIP."
    }

  ],

  "Services à bord": [

    {
      question: "Je peux charger mon téléphone dans le bus ?",

      keywords: [
        "charger téléphone",
        "charger mon téléphone",
        "charge téléphone",
        "port usb",
        "usb",
        "wifi",
        "wi-fi"
      ],

      answer:
        "Oui mon combi ! Il y a des ports USB ainsi qu'un Wi-Fi haut débit pour te permettre de rester connecté pendant ton trajet."
    }

  ],

  "Promotions": [

    {
      question: "Quelles promotions puis-je avoir pour mon voyage ?",

      keywords: [
        "promotion",
        "promotions",
        "réduction",
        "réductions",
        "avantage",
        "offre"
      ],

      answer:
        "Les différentes promotions proposées par les agences sont disponibles sur la plateforme selon ton choix."
    }

  ],

  "Paiement": [

    {
      question: "Je peux payer ma réservation en ligne ?",

      keywords: [
        "payer réservation",
        "payer ma réservation",
        "paiement en ligne",
        "payer en ligne",
        "orange money",
        "mtn mobile money",
        "mobile money",
        "carte bancaire"
      ],

      answer:
        "Oui ! Tu peux finaliser ta réservation avec Orange Money, MTN Mobile Money, une carte bancaire ou en espèces."
    },

    {
      question: "Y a-t-il des frais pour mon paiement en ligne ?",

      keywords: [
        "frais paiement",
        "frais paiement en ligne",
        "frais en ligne",
        "frais supplémentaires",
        "frais supplémentaires paiement"
      ],

      answer:
        "Selon l'agence choisie, les éventuels frais marchands de chaque opérateur seront indiqués."
    }

  ],

  "Bagages": [

    {
      question: "Je peux voyager avec combien de bagages ?",

      keywords: [
        "combien bagages",
        "nombre bagages",
        "bagages autorisés",
        "bagage",
        "bagages",
        "23 kg",
        "23kg"
      ],

      answer:
        "Tu as droit à deux sacs de 23 kg. Pour les bagages supplémentaires, le tarif est de 300 F par kg."
    },

    {
      question: "Que faire si mon bagage arrive abîmé ?",

      keywords: [
        "bagage abîmé",
        "bagage abime",
        "bagage cassé",
        "bagage endommagé",
        "bagage perdu"
      ],

      answer:
        "Si ton bagage arrive abîmé, contacte directement l'agence afin qu'elle puisse prendre en charge la situation."
    }

  ]

  }, /* fin fr */

  en: {

    "Travel": [

      {
        question: "When does my next bus leave?",

        keywords: [
          "next bus",
          "next departure",
          "departure time",
          "bus leaves",
          "bus leave",
          "departure"
        ],

        answer:
          "Hey, check the departure time on the agency page you selected. If you'd like, I can also help you find that information."
      },

      {
        question: "I missed my bus, can I take the next one?",

        keywords: [
          "missed my bus",
          "miss my bus",
          "missed bus",
          "next bus",
          "take the next one",
          "take next bus"
        ],

        answer:
          "If you missed your bus, contact the agency directly or send them a WhatsApp message to confirm whether you can take the next departure."
      },

      {
        question: "Can I pay for my return trip now?",

        keywords: [
          "pay for my return",
          "pay return",
          "return trip now",
          "return ticket"
        ],

        answer:
          "Yes! You can pay for your return trip at the same time and get a discount on the transaction."
      },

      {
        question: "When should I arrive before departure?",

        keywords: [
          "how long before",
          "time before departure",
          "arrive before",
          "arrive before departure",
          "when to arrive",
          "30 minutes"
        ],

        answer:
          "I'd recommend arriving 30 minutes before departure so you can take care of boarding formalities without rushing."
      },

      {
        question: "What happens if my bus breaks down?",

        keywords: [
          "bus breaks down",
          "bus broke down",
          "breakdown",
          "bus break down",
          "bus problem"
        ],

        answer:
          "No worries! The platform shares your location with the agency, so they can send another bus to help you continue your trip."
      }

    ],

    "Cancellation": [

      {
        question: "Can I get a refund if I cancel my trip?",

        keywords: [
          "refund",
          "refunded",
          "cancel trip",
          "cancellation",
          "cancel my trip"
        ],

        answer:
          "You may be able to reschedule your trip or travel on a later date. To find out the exact conditions, contact the relevant agency."
      }

    ],

    "Fares": [

      {
        question: "What's the fare for my child?",

        keywords: [
          "child fare",
          "child price",
          "child",
          "fare for my child",
          "kids fare"
        ],

        answer:
          "The child fare is only available in VIP class."
      }

    ],

    "Onboard Services": [

      {
        question: "Can I charge my phone on the bus?",

        keywords: [
          "charge my phone",
          "charge phone",
          "usb port",
          "usb",
          "wifi",
          "wi-fi"
        ],

        answer:
          "Yes! There are USB ports as well as high-speed Wi-Fi so you can stay connected throughout your trip."
      }

    ],

    "Promotions": [

      {
        question: "What promotions can I get for my trip?",

        keywords: [
          "promotion",
          "promotions",
          "discount",
          "discounts",
          "deal",
          "offer"
        ],

        answer:
          "The various promotions offered by the agencies are available on the platform depending on your choice."
      }

    ],

    "Payment": [

      {
        question: "Can I pay for my reservation online?",

        keywords: [
          "pay for my reservation",
          "pay reservation",
          "online payment",
          "pay online",
          "orange money",
          "mtn mobile money",
          "mobile money",
          "credit card",
          "card payment"
        ],

        answer:
          "Yes! You can complete your reservation with Orange Money, MTN Mobile Money, a bank card, or cash."
      },

      {
        question: "Are there fees for online payment?",

        keywords: [
          "payment fees",
          "online payment fees",
          "fees online",
          "extra fees",
          "additional fees payment"
        ],

        answer:
          "Depending on the agency you choose, any merchant fees charged by each operator will be shown."
      }

    ],

    "Luggage": [

      {
        question: "How much luggage can I travel with?",

        keywords: [
          "how much luggage",
          "luggage allowed",
          "luggage",
          "baggage",
          "23 kg",
          "23kg"
        ],

        answer:
          "You're allowed two 23 kg bags. For extra luggage, the fee is 300 F per kg."
      },

      {
        question: "What if my luggage arrives damaged?",

        keywords: [
          "luggage damaged",
          "bag damaged",
          "luggage broken",
          "damaged bag",
          "lost luggage"
        ],

        answer:
          "If your luggage arrives damaged, contact the agency directly so they can take care of the situation."
      }

    ]

  } /* fin en */

}


/* =========================================================
   BASE DE CONNAISSANCES
========================================================= */

const ALL_FAQ = {

  fr: Object.values(TRAVEL_FAQ.fr).flat(),

  en: Object.values(TRAVEL_FAQ.en).flat()

}


/* =========================================================
   NORMALISATION DES TEXTES
========================================================= */

const normalizeText = (text) => {

  return String(text || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[?!.,;:'"()]/g, " ")
    .replace(/\s+/g, " ")
    .trim()

}


/* =========================================================
   TOLÉRANCE AUX FAUTES D'ORTHOGRAPHE
   Combi ne doit pas être bloqué par une faute de frappe ("bonjr" au
   lieu de "bonjour", "vehicul" au lieu de "véhicule"...). On utilise
   la distance de Levenshtein (nombre minimal de lettres à changer
   pour passer d'un mot à l'autre) pour tolérer quelques fautes, avec
   une marge qui grandit avec la longueur du mot.
========================================================= */

const levenshteinDistance = (a, b) => {

  if (a === b) {
    return 0
  }

  const al = a.length
  const bl = b.length

  if (al === 0) {
    return bl
  }

  if (bl === 0) {
    return al
  }

  const matrix =
    Array.from(
      { length: al + 1 },
      () => new Array(bl + 1).fill(0)
    )

  for (let i = 0; i <= al; i++) {
    matrix[i][0] = i
  }

  for (let j = 0; j <= bl; j++) {
    matrix[0][j] = j
  }

  for (let i = 1; i <= al; i++) {
    for (let j = 1; j <= bl; j++) {

      const cost =
        a[i - 1] === b[j - 1] ? 0 : 1

      matrix[i][j] =
        Math.min(
          matrix[i - 1][j] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j - 1] + cost
        )

    }
  }

  return matrix[al][bl]

}


// Nombre de fautes tolérées selon la longueur du mot cible
const maxAllowedTypos = (word) => {

  // Sur un mot très court (2-3 lettres), même 1 seule lettre changée
  // peut transformer le mot en un autre mot totalement différent.
  // On exige donc une correspondance exacte pour ces mots-là, et on
  // réserve la tolérance aux mots plus longs.
  if (word.length <= 3) return 0
  if (word.length <= 6) return 1
  if (word.length <= 10) return 2
  return 3

}


// Un mot tapé par l'utilisateur correspond-il (exactement ou à une
// faute près) à un mot cible ?
const fuzzyWordMatch = (word, target) => {

  if (!word || !target) {
    return false
  }

  if (word === target) {
    return true
  }

  if (Math.abs(word.length - target.length) > maxAllowedTypos(target)) {
    return false
  }

  return levenshteinDistance(word, target) <= maxAllowedTypos(target)

}


// Une expression cible ("prochain bus") est-elle présente dans la
// phrase de l'utilisateur, mot par mot, avec tolérance aux fautes
// sur chaque mot ?
const fuzzyPhraseIncluded = (words, phrase) => {

  const phraseWords =
    phrase.split(" ").filter(Boolean)

  return phraseWords.every(
    (pw) => words.some((w) => fuzzyWordMatch(w, pw))
  )

}


/* =========================================================
   RECHERCHE DANS LA FAQ
========================================================= */

const findFaqAnswer = (userQuestion, locale = "fr") => {

  const question = normalizeText(userQuestion)

  if (!question) {
    return null
  }

  const words =
    question.split(" ").filter(Boolean)

  const faqList =
    ALL_FAQ[locale] || ALL_FAQ.fr

  let bestMatch = null
  let bestScore = 0

  faqList.forEach((faq) => {

    let score = 0

    faq.keywords.forEach((keyword) => {

      const normalizedKeyword = normalizeText(keyword)

      // Correspondance exacte (sous-chaîne) OU tolérante aux fautes
      if (
        question.includes(normalizedKeyword) ||
        fuzzyPhraseIncluded(words, normalizedKeyword)
      ) {
        score += normalizedKeyword.length
      }

    })

    const normalizedFaqQuestion = normalizeText(faq.question)

    if (question === normalizedFaqQuestion) {
      score += 100
    }

    if (score > bestScore) {
      bestScore = score
      bestMatch = faq
    }

  })

  return bestScore > 0 ? bestMatch : null
}


// Un mot de la phrase de l'utilisateur correspond-il (exactement ou
// à une faute près) à AU MOINS UN des mots cibles ?
const fuzzyIncludesAny = (words, targets) =>
  words.some(
    (w) => targets.some((target) => fuzzyWordMatch(w, target))
  )


/* =========================================================
   DÉTECTION DE SALUTATION
   Permet à Combi de reconnaître un simple "salut"/"hey"/"bonsoir" et
   d'y répondre naturellement, plutôt que de le traiter comme une
   question à chercher dans la FAQ.
========================================================= */

const GREETING_WORDS = [
  "salut",
  "bonjour",
  "bonsoir",
  "coucou",
  "cc",
  "slt",
  "bsr",
  "bjr",
  "yo",
  "wesh",
  "allo",
  "hello",
  "hi",
  "hey",
  "hola"
]

const GREETING_PHRASES = [
  "good morning",
  "good evening",
  "good afternoon"
]

const isGreeting = (userInput) => {

  const question = normalizeText(userInput)

  if (!question) {
    return false
  }

  const words =
    question.split(" ").filter(Boolean)

  // On limite aux messages courts pour éviter qu'une vraie question
  // contenant incidemment un de ces mots ("bonjour, comment activer...")
  // ne soit prise à tort pour une simple salutation.
  if (words.length > 4) {
    return false
  }

  if (fuzzyIncludesAny(words, GREETING_WORDS)) {
    return true
  }

  return GREETING_PHRASES.some(
    (p) => question.includes(p)
  )

}


/* =========================================================
   PETITE CONVERSATION : "comment tu vas ?" / "qui es-tu ?"
========================================================= */

const HOW_ARE_YOU_PHRASES = [
  "comment tu vas",
  "comment vas tu",
  "comment ca va",
  "ca va",
  "tu vas bien",
  "comment vous allez",
  "vous allez bien",
  "how are you",
  "how you doing",
  "you good",
  "you ok",
  "are you well"
]

const isHowAreYou = (userInput) => {

  const question = normalizeText(userInput)

  if (!question) {
    return false
  }

  const words =
    question.split(" ").filter(Boolean)

  if (words.length > 6) {
    return false
  }

  return HOW_ARE_YOU_PHRASES.some(
    (p) =>
      question.includes(p) ||
      fuzzyPhraseIncluded(words, p)
  )

}

const IDENTITY_PHRASES = [
  "qui es tu",
  "tu es qui",
  "t es qui",
  "c est qui",
  "qui est tu",
  "tu es quoi",
  "c est quoi combi",
  "who are you",
  "what are you",
  "what is combi",
  "who is combi"
]

const isIdentityQuestion = (userInput) => {

  const question = normalizeText(userInput)

  if (!question) {
    return false
  }

  const words =
    question.split(" ").filter(Boolean)

  if (words.length > 6) {
    return false
  }

  return IDENTITY_PHRASES.some(
    (p) =>
      question.includes(p) ||
      fuzzyPhraseIncluded(words, p)
  )

}


/* =========================================================
   POLITESSE : "merci", "merci c'est gentil", "s'il te plaît"...
   Permet à Combi de répondre chaleureusement plutôt que de traiter un
   simple remerciement comme une question à chercher dans la FAQ.
========================================================= */

const THANKS_WORDS = [
  "merci",
  "remercie",
  "gentil",
  "thanks",
  "thank",
  "thankyou"
]

const THANKS_PHRASES = [
  "merci beaucoup",
  "merci c est gentil",
  "c est gentil",
  "s il te plait",
  "s il vous plait",
  "thank you",
  "thanks a lot",
  "you re welcome"
]

const isThanks = (userInput) => {

  const question = normalizeText(userInput)

  if (!question) {
    return false
  }

  const words =
    question.split(" ").filter(Boolean)

  if (words.length > 6) {
    return false
  }

  if (fuzzyIncludesAny(words, THANKS_WORDS)) {
    return true
  }

  return THANKS_PHRASES.some(
    (p) =>
      question.includes(p) ||
      fuzzyPhraseIncluded(words, p)
  )

}


// Réponse positive/neutre à la question "et toi, ça va ?" que Combi
// vient de poser — utilisé uniquement juste après le bloc
// "how_are_you_reply" pour savoir si on enchaîne naturellement ou si
// on retombe sur le routage normal (menu, salutation, question...).
const POSITIVE_FEELING_WORDS = [
  "bien",
  "tresbien",
  "nickel",
  "super",
  "cool",
  "pasmal",
  "caroule",
  "cava",
  "impeccable",
  "good",
  "great",
  "fine",
  "well",
  "notbad",
  "ok",
  "okay",
  "parfait"
]

const isPositiveFeelingReply = (userInput) => {

  const question =
    normalizeText(userInput).replace(/\s+/g, "")

  if (!question) {
    return false
  }

  return POSITIVE_FEELING_WORDS.some(
    (w) => question.includes(w)
  )

}


// Choisit une variante au hasard dans une liste de réponses (pour ne
// pas répéter toujours la même phrase).
const pickRandom = (list) =>
  list[Math.floor(Math.random() * list.length)]


/* =========================================================
   DÉTECTION DES SUJETS DU MENU PRINCIPAL
   Permet de reconnaître "Que propose Metua'a ?" / "Comment devenir
   chauffeur ?" / "Comment créer mon compte ?" même tapés librement
   au clavier, pas seulement cliqués comme options du menu.
========================================================= */

const MENU_TOPIC_KEYWORDS = {

  services: {
    fr: [
      "que propose metua a",
      "que propose la plateforme",
      "quels sont les services",
      "que faites vous",
      "c est quoi metua a",
      "a quoi sert metua a",
      "vos fonctionnalites",
      "que peut faire la plateforme"
    ],
    en: [
      "what does metua a offer",
      "what does the platform offer",
      "what services",
      "what do you do",
      "what is metua a",
      "what can the platform do",
      "your features"
    ]
  },

  chauffeur: {
    fr: [
      "devenir chauffeur",
      "comment devenir chauffeur",
      "chauffeur partenaire",
      "conduire avec metua a",
      "je veux devenir chauffeur",
      "comment etre chauffeur"
    ],
    en: [
      "become a driver",
      "how to become a driver",
      "partner driver",
      "drive with metua a",
      "i want to become a driver",
      "how do i become a driver"
    ]
  },

  reserver: {
    fr: [
      "reserver mon trajet",
      "comment reserver",
      "faire une reservation",
      "reserver une course",
      "comment faire pour reserver",
      "comment reserver un trajet"
    ],
    en: [
      "book my ride",
      "how to book",
      "make a reservation",
      "book a ride",
      "how do i book",
      "how to book a ride"
    ]
  },

  inscription: {
    fr: [
      "creer mon compte",
      "creer un compte",
      "comment m inscrire",
      "ouvrir un compte",
      "faire mon inscription",
      "je veux m inscrire",
      "comment s inscrire"
    ],
    en: [
      "create my account",
      "create an account",
      "how to sign up",
      "open an account",
      "register an account",
      "i want to sign up"
    ]
  },

  voyages: {
    fr: [
      "question sur mon voyage",
      "a propos de mon voyage",
      "concernant mon voyage",
      "question sur mon trajet"
    ],
    en: [
      "question about my trip",
      "about my trip",
      "concerning my trip",
      "question about my ride"
    ]
  },

  // Détection en texte libre : les deux topics ci-dessous mènent
  // directement aux blocs "compte_solde"/"compte_profil" (données
  // live du compte connecté, voir plus bas). Un visiteur non connecté
  // qui tape ça atterrit quand même sur ces blocs, qui savent répondre
  // proprement avec une invitation à se connecter (voir isAuthenticated
  // dans buildFlow).
  compte_solde: {
    fr: [
      "mon solde",
      "solde de mon compte",
      "solde de mon portefeuille",
      "combien j ai sur mon compte",
      "combien j ai dans mon portefeuille",
      "quel est mon solde"
    ],
    en: [
      "my balance",
      "my wallet balance",
      "how much do i have",
      "what is my balance",
      "account balance"
    ]
  },

  compte_profil: {
    fr: [
      "mon profil",
      "mes informations",
      "mes informations personnelles",
      "voir mon profil",
      "mes coordonnees"
    ],
    en: [
      "my profile",
      "my information",
      "my personal information",
      "view my profile",
      "my details"
    ]
  },

  // Détection en texte libre pour les autres produits Metua'a — mène
  // directement à la réponse dédiée (bloc "produit_xxx", voir plus
  // bas), sans passer par le sous-menu "produits".
  produit_eats: {
    fr: [
      "c est quoi metua a eats",
      "qu est ce que metua a eats",
      "livraison de repas",
      "commander a manger",
      "metua a eat",
      "comment marche metua a eats"
    ],
    en: [
      "what is metua a eats",
      "food delivery",
      "order food",
      "how does metua a eats work"
    ]
  },

  produit_mappers: {
    fr: [
      "c est quoi metua a mappers",
      "qu est ce que metua a mappers",
      "devenir mapper",
      "cartographier mon quartier",
      "comment marche metua a mappers",
      "metua a mapper"
    ],
    en: [
      "what is metua a mappers",
      "become a mapper",
      "map my neighborhood",
      "how does metua a mappers work"
    ]
  },

  produit_entreprise: {
    fr: [
      "c est quoi metua a entreprise",
      "qu est ce que metua a entreprise",
      "solution pour entreprise",
      "metua a business",
      "comment marche metua a entreprise"
    ],
    en: [
      "what is metua a enterprise",
      "business solution",
      "metua a business",
      "how does metua a enterprise work"
    ]
  },

  produit_mplus: {
    fr: [
      "c est quoi le programme m plus",
      "qu est ce que le programme m plus",
      "programme m plus",
      "devenir proprietaire de mon vehicule",
      "comment marche le programme m plus"
    ],
    en: [
      "what is the m plus program",
      "m plus program",
      "become a vehicle owner",
      "how does the m plus program work"
    ]
  },

  produit_travelhub: {
    fr: [
      "c est quoi travel hub",
      "qu est ce que travel hub",
      "voyage interurbain",
      "covoiturage metua a",
      "comment marche travel hub",
      "metua a travel hub"
    ],
    en: [
      "what is travel hub",
      "intercity travel",
      "carpooling metua a",
      "how does travel hub work"
    ]
  },

  // Détection en texte libre pour les rubriques de l'espace client
  // connecté (sidebar du dashboard, voir plus bas). Phrases préfixées
  // "rubrique"/"section" pour ne pas entrer en collision avec
  // compte_solde ("mon solde") qui reste plus direct/prioritaire.
  espace_client: {
    fr: [
      "fonctionnalites de mon espace",
      "rubriques de mon espace",
      "que puis je faire dans mon espace",
      "mon espace client",
      "les rubriques de mon compte"
    ],
    en: [
      "my account features",
      "my dashboard sections",
      "what can i do in my account",
      "my client area"
    ]
  },

  espace_dashboard: {
    fr: ["rubrique tableau de bord", "section accueil de mon espace"],
    en: ["dashboard section", "home section of my account"]
  },

  espace_rides: {
    fr: ["rubrique courses", "section courses de mon espace", "demander une course"],
    en: ["rides section", "request a ride section"]
  },

  espace_myrides: {
    fr: ["rubrique mes courses", "section mes courses"],
    en: ["my rides section"]
  },

  espace_bookings: {
    fr: ["rubrique mes reservations", "section mes reservations", "section reservations"],
    en: ["my bookings section", "bookings section"]
  },

  espace_wallet: {
    fr: ["rubrique portefeuille", "section portefeuille de mon espace"],
    en: ["wallet section", "wallet section of my account"]
  },

  espace_transactions: {
    fr: ["rubrique transactions", "section transactions", "rubrique paiements"],
    en: ["transactions section", "payments section"]
  },

  espace_favoris: {
    fr: ["rubrique favoris", "section favoris de mon espace"],
    en: ["favorites section"]
  },

  espace_profil: {
    fr: ["rubrique profil de mon espace", "section profil de mon espace"],
    en: ["profile section of my account"]
  },

  espace_parametres: {
    fr: ["rubrique parametres", "section parametres de mon espace"],
    en: ["settings section", "settings section of my account"]
  }

}


// Retourne le nom du bloc ("services" | "chauffeur" | "reserver" |
// "inscription" | "voyages" | "compte_solde" | "compte_profil" |
// "produit_eats" | "produit_mappers" | "produit_entreprise" |
// "produit_mplus" | "produit_travelhub") selon le sujet détecté dans
// le texte libre de l'utilisateur (avec tolérance aux fautes), ou
// null si rien ne correspond clairement. En cas de plusieurs
// correspondances, la phrase-clé la plus longue (donc la plus
// précise) l'emporte.
const matchMenuTopic = (userInput, locale) => {

  const question = normalizeText(userInput)

  if (!question) {
    return null
  }

  const words =
    question.split(" ").filter(Boolean)

  const lang =
    locale === "en" ? "en" : "fr"

  let bestTopic = null
  let bestScore = 0

  Object.entries(MENU_TOPIC_KEYWORDS).forEach(([topic, byLocale]) => {

    const phrases =
      byLocale[lang] || byLocale.fr

    phrases.forEach((phrase) => {

      const matches =
        question.includes(phrase) ||
        fuzzyPhraseIncluded(words, phrase)

      if (matches && phrase.length > bestScore) {
        bestScore = phrase.length
        bestTopic = topic
      }

    })

  })

  return bestTopic

}


// Routeur partagé pour tout texte libre tapé par l'utilisateur (pas
// un clic sur une option) : détecte dans l'ordre "comment tu vas ?",
// "qui es-tu ?", "merci", une simple salutation, puis un des sujets
// du menu principal tapés librement, puis retombe sur la recherche
// FAQ (bloc "question"). Utilisé à chaque point d'entrée en texte
// libre du flow, pour que Combi reconnaisse ces phrases où qu'on les
// tape (pas seulement au tout premier message).
const resolveFreeTextBlock = (userInput, locale = "fr") => {

  if (isHowAreYou(userInput)) {
    return "how_are_you_reply"
  }

  if (isIdentityQuestion(userInput)) {
    return "identity_reply"
  }

  if (isThanks(userInput)) {
    return "thanks_reply"
  }

  if (isGreeting(userInput)) {
    return "greeting_reply"
  }

  const topic =
    matchMenuTopic(userInput, locale)

  if (topic) {
    return topic
  }

  return "question"

}


/* =========================================================
   TEXTES DU CHATBOT
========================================================= */

const CHATBOT_TEXT = {

  fr: {

    tooltip: "Discutez avec votre Combi",

    headerTitle: "Combi",

    // Année dynamique (jamais figée) + texte réellement en français
    // (avant, les deux locales affichaient le même texte anglais codé
    // en dur avec "2023"). Fonction plutôt que chaîne statique, même
    // motif que web-lewootrack (t.footerText(title)) même si ici pas
    // besoin de paramètre de marque.
    footerText:
      () => `Copyright © ${new Date().getFullYear()} Metua'a. Tous droits réservés.`,

    inputPlaceholder:
      "Écris-moi un message...",

    loadHistory:
      "Charger mon historique ⟳",

    greetingGuest:
      "Salut ! 👋 Moi, c’est Combi, ton assistant Metua’a. Je suis là pour toi. Qu’est-ce qu’on fait aujourd’hui ?",

    menu: {

      services:
        "Que propose Metua'a ?",

      servicesConnecte:
        "Découvrir nos services",

      chauffeur:
        "Comment devenir chauffeur ?",

      chauffeurConnecte:
        "Devenir chauffeur partenaire",

      reserver:
        "Comment réserver mon trajet ?",

      reserverConnecte:
        "Réserver un nouveau trajet",

      inscription:
        "Comment créer mon compte ?",

      voyages:
        "J'ai une question sur mon voyage",

      compte:
        "Mon compte",

      compteSolde:
        "Solde de mon portefeuille",

      compteProfil:
        "Mes infos de profil",

      espaceClient:
        "Fonctionnalités de mon espace",

      produits:
        "Découvrir nos produits",

      produitEats:
        "Metua'a Eats",

      produitMappers:
        "Metua'a Mappers",

      produitEntreprise:
        "Metua'a Entreprise",

      produitMplus:
        "Programme M+",

      produitTravelHub:
        "Metua'a Travel Hub",

      autre:
        "J'ai une autre question",

      retour:
        "Retour au menu",

      retourCategories:
        "Retour aux catégories",

      retry:
        "Réessayer"

    },

    reponses: {

      services:
        "Metua'a, c’est bien plus qu’un service de transport ! 🚀",

      chauffeur:
        "Tu veux devenir chauffeur partenaire ? 🚗",

      chauffeurOutro:
        "Si tu veux, mon combi, je peux t'expliquer davantage.",

      reserver:
        "Pour réserver ton trajet, c’est simple ! 🛺\n\nOuvre Metua’a Travel Hub, choisis ton point de départ et ta destination, puis sélectionne le chauffeur disponible qui te convient.",

      inscription:
        "Créer ton compte Metua’a est gratuit et prend moins de 2 minutes ! 🚀\n\nClique sur « Inscrivez-vous » sur la page d’accueil, renseigne tes informations et c’est parti !\nTu peux aussi passer par « Connexion » en haut de n’importe quelle page, puis cliquer sur « S’inscrire » en bas du formulaire.",

      produitsIntro:
        "Quel produit veux-tu découvrir, mon combi ? 👇",

      produitEatsIntro:
        "Metua'a Eats, c’est la livraison de repas près de chez toi ! 🍔",

      produitMappersIntro:
        "Metua'a Mappers, c’est cartographier ton quartier et être payé pour ça ! 🗺️",

      produitTravelHubIntro:
        "Metua'a Travel Hub connecte voyageurs et compagnies de transport interurbain ! 🛺",

      produitEntrepriseIntro:
        "Metua'a Entreprise simplifie les déplacements de vos équipes et la gestion de vos opérations ! 🤝",

      produitMplusIntro:
        "Le Programme M+ en un coup d’œil : 🚙",

      espaceClientIntro:
        "Voici les rubriques disponibles dans ton espace, mon combi : 👇",

      espaceClientNavHint:
        "Tu la trouveras dans le menu de gauche de ton espace client."

    },

    voyagesIntro:
      "Bien sûr mon combi ! 👋 Sur quel sujet veux-tu qu’on échange ?",

    autreQuestion:
      "Vas-y mon combi ! 😊 Pose-moi ta question directement dans la zone de texte. Je vais regarder dans ma base de connaissances pour te répondre.",

    questionNonTrouvee:
      "Hmm 🤔 Je n’ai pas encore cette information dans ma base de connaissances. Si tu veux, reformule ta question avec un peu plus de détails ou contacte notre support.",

    ctaInscription:
      "\n\n✨ Petit conseil de ton Combi : avec ton compte Metua'a, tu peux réserver en un clic et suivre tes trajets en temps réel.",

    greetingReply: [
      "Salut mon combi ! 👋 Comment je peux t'aider aujourd'hui ?",
      "Hey ! 😊 Content de te voir. Qu'est-ce qu'on fait ?",
      "Coucou ! 👋 Je suis là, dis-moi tout."
    ],

    howAreYouReply: [
      "Ça va super bien, merci de demander ! 😊 Et toi, ça va ?",
      "Toujours en forme pour t'aider, mon combi ! 💪 Et toi, comment ça va ?"
    ],

    howAreYouContinue: (name) => [
      `Content de l'entendre${name ? " " + name : ""} ! 😊 Alors, qu'est-ce que je peux faire pour toi ?`,
      "Parfait ! 🚀 Dis-moi ce dont tu as besoin."
    ],

    identityReply: [
      "Je suis Combi, l'assistant Metua'a ! 🤖 Je réponds à tes questions sur la plateforme, je t'aide à réserver ou à devenir chauffeur partenaire.",
      "Moi ? Je suis Combi ! 👋 Ton assistant Metua'a, toujours prêt à t'aider."
    ],

    thanksReply: [
      "Avec plaisir mon combi ! 😊 N'hésite pas si tu as d'autres questions.",
      "De rien ! 🚀 Je suis là si tu as besoin d'autre chose."
    ],

    compteIntro:
      "Qu'est-ce que tu veux consulter, mon combi ? 👇",

    compteSoldeIntro:
      "Voici le solde de ton portefeuille : 💰",

    compteProfilIntro:
      "Voici tes informations de profil : 👤",

    compteConnexionRequise:
      "Cette info est réservée aux comptes connectés. Connecte-toi à ton compte Metua'a pour que je puisse te répondre ! 🔒",

    compteErreur:
      "Hmm 🤔 je n'arrive pas à récupérer cette information pour le moment. Réessaie un peu plus tard ou contacte notre support.",

    compteSoldeAucun:
      "Je ne trouve aucun portefeuille associé à ton compte pour l'instant.",

    compteProfilVide:
      "Je n'ai pas encore assez d'informations de profil à te montrer."

  },


  en: {

    tooltip:
      "Chat with your Combi",

    headerTitle:
      "Combi",

    footerText:
      () => `Copyright © ${new Date().getFullYear()} Metua'a. All rights reserved.`,

    inputPlaceholder:
      "Write me a message...",

    loadHistory:
      "Load my history ⟳",

    greetingGuest:
      "Hi! 👋 I’m Combi, your Metua’a assistant. I’m here to help. What can we do today?",

    menu: {

      services:
        "What does Metua'a offer?",

      servicesConnecte:
        "Explore our services",

      chauffeur:
        "How do I become a driver?",

      chauffeurConnecte:
        "Become a partner driver",

      reserver:
        "How do I book my ride?",

      reserverConnecte:
        "Book a new ride",

      inscription:
        "How do I create my account?",

      voyages:
        "I have a question about my trip",

      compte:
        "My account",

      compteSolde:
        "My wallet balance",

      compteProfil:
        "My profile info",

      espaceClient:
        "My account features",

      produits:
        "Explore our products",

      produitEats:
        "Metua'a Eats",

      produitMappers:
        "Metua'a Mappers",

      produitEntreprise:
        "Metua'a Enterprise",

      produitMplus:
        "M+ Program",

      produitTravelHub:
        "Metua'a Travel Hub",

      autre:
        "I have another question",

      retour:
        "Back to menu",

      retourCategories:
        "Back to categories",

      retry:
        "Try again"

    },

    reponses: {

      services:
        "Metua'a is more than just transportation! 🚀\n\n🛺 Metua'a Travel Hub — urban and intercity rides\n🍔 Metua'a Eat — food delivery\n🚗 Metua'a Buy — vehicle buying and selling\n🗺️ Metua'a Mapper — mapping and real-time tracking",

      chauffeur:
        "Want to become a partner driver? 🚗",

      chauffeurOutro:
        "If you'd like, I can tell you more.",

      reserver:
        "Booking your ride is simple! 🛺\n\nOpen Metua’a Travel Hub, choose your pickup point and destination, then select the available driver that suits you.",

      inscription:
        "Creating your Metua'a account is free and takes less than 2 minutes! 🚀\n\nClick « Sign up » on the homepage, enter your information and you're ready to go!\nYou can also go through « Log in » at the top of any page, then click « Sign up » at the bottom of the form.",

      produitsIntro:
        "Which product would you like to explore, my combi? 👇",

      produitEntrepriseIntro:
        "Metua'a Enterprise simplifies your team's travel and operations management! 🤝",

      produitMplusIntro:
        "The M+ Program at a glance: 🚙",

      produitEatsEn:
        "Metua'a Eats is our food delivery service — order your favorite dishes and get them delivered fresh! 🍔\n\n• Top Chef Selection — Access trusted home-cooked meals, from renowned Akwa restaurants to your neighborhood's favorite spot.\n• Insulated & Sealed Guarantee — Our couriers use reinforced insulated boxes so your meal arrives at the perfect temperature.\n• Real-time GPS tracking — Watch your meal's journey live on the map, from the kitchen to your table.\n• Kmer's Chef Ranking — Vote for your favorite restaurants and earn loyalty points redeemable for free meals.",

      produitMappersEn:
        "Metua'a Mappers lets you map your own neighborhood and earn money helping the community get around better! 🗺️\n\n• A 100% African solution — Built for the Cameroonian context: motorbikes, cars, trucks, and services adapted to your reality.\n• Secure, lifetime payments — Withdraw your earnings via Mobile Money safely through our built-in Wallet. Map once, earn commissions for life every time it's used!\n• Metua'a Rewards — A loyalty and bonus program: every ride and every mission earns you exclusive perks.",

      produitTravelHubEn:
        "Metua'a Travel Hub connects travelers with intercity and carpool transport companies! 🛺\n\n• For travelers — Search a route, compare offers, book and pay for your ticket online securely, and track your trip in real time.\n• For transport companies — Digitalize your operations by managing your fleet, drivers, routes, and bookings from a single interface.\n• Safer, smarter mobility — Every partner company is verified before joining the platform, guaranteeing a reliable, secure service.\n• Metua'a Rewards — A loyalty and bonus program: every ride and every mission earns you exclusive perks.",

      espaceClientIntro:
        "Here are the sections available in your account, my combi: 👇",

      espaceClientNavHint:
        "You'll find it in the left-hand menu of your account area."

    },

    voyagesIntro:
      "Of course, my combi! 👋 What would you like us to look at?",

    autreQuestion:
      "Go ahead, my combi! 😊 Ask me your question directly in the text box. I'll check my knowledge base and try to help you.",

    questionNonTrouvee:
      "Hmm 🤔 I don't have that information in my knowledge base yet. Try asking your question with a little more detail or contact our support.",

    ctaInscription:
      "\n\n✨ A little tip from Combi: with your Metua'a account, you can book in one click and track your rides in real time.",

    greetingReply: [
      "Hey! 👋 What can I help you with today?",
      "Hi there! 😊 Good to see you. What are we doing?",
      "Hey! 👋 I'm here, tell me what's up."
    ],

    howAreYouReply: [
      "Doing great, thanks for asking! 😊 How about you?",
      "Always ready to help! 💪 How are you doing?"
    ],

    howAreYouContinue: (name) => [
      `Glad to hear it${name ? " " + name : ""}! 😊 So, what can I do for you?`,
      "Perfect! 🚀 Tell me what you need."
    ],

    identityReply: [
      "I'm Combi, the Metua'a assistant! 🤖 I answer your questions about the platform, help you book a ride, or become a partner driver.",
      "Me? I'm Combi! 👋 Your Metua'a assistant, always ready to help."
    ],

    thanksReply: [
      "You're welcome! 😊 Let me know if you have other questions.",
      "No problem! 🚀 I'm here if you need anything else."
    ],

    compteIntro:
      "What would you like to check? 👇",

    compteSoldeIntro:
      "Here's your wallet balance: 💰",

    compteProfilIntro:
      "Here's your profile info: 👤",

    compteConnexionRequise:
      "This information is only available for logged-in accounts. Log in to your Metua'a account so I can help! 🔒",

    compteErreur:
      "Hmm 🤔 I can't retrieve this information right now. Please try again later or contact our support.",

    compteSoldeAucun:
      "I can't find any wallet linked to your account yet.",

    compteProfilVide:
      "I don't have enough profile information to show you yet."

  }

}


/* =========================================================
   SALUTATIONS UTILISATEUR CONNECTÉ
========================================================= */

const GREETING_TEMPLATES = {

  fr: (name) => [

    `Salut ${name} ! 👋 C’est Combi. Comment vas-tu aujourd’hui ?`,

    `Hey ${name} ! 😊 Content de te revoir. Qu’est-ce que je peux faire pour toi ?`,

    `Salut ${name} ! 👋 Ton Combi est là. De quoi as-tu besoin ?`,

    `Bienvenue ${name} ! 🚀 On regarde ça ensemble ?`

  ],

  en: (name) => [

    `Hi ${name}! 👋 It’s Combi. How are you today?`,

    `Hey ${name}! 😊 Great to see you again. What can I do for you?`,

    `Hi ${name}! 👋 Your Combi is here. What do you need?`,

    `Welcome back ${name}! 🚀 Shall we look at that together?`

  ]

}


const getDisplayName = (user) => {

  if (!user) return null

  const name =
    user.firstName ||
    user.first_name ||
    user.prenom ||
    user.name ||
    user.fullName ||
    user.full_name ||
    user.lastName ||
    null

  return name ? String(name).trim() : null
}


const pickGreeting = (locale, name) => {

  const templates =
    GREETING_TEMPLATES[locale](name)

  return templates[
    Math.floor(Math.random() * templates.length)
  ]

}


/* =========================================================
   CTA INSCRIPTION
========================================================= */

const withCta = (
  message,
  locale,
  isAuthenticated
) => {

  if (isAuthenticated) {
    return message
  }

  return message +
    CHATBOT_TEXT[locale].ctaInscription

}


/* =========================================================
   ÉCOSYSTÈME METUA'A (vrai contenu de la page vitrine)
========================================================= */

// Un petit emoji par service, pour l'affichage dans le chat (les
// icônes de la page sont des composants React, pas utilisables tels
// quels dans un message texte).
const ECOSYSTEM_SERVICE_EMOJI = {
  mapper: "🗺️",
  eat: "🍔",
  travel: "🛺",
  driver: "🚗",
  enterprise: "🤝",
  mplus: "🚙"
}

// Construit la liste des services à partir du VRAI tableau de la page
// "Metua'a Travel Hub" (voir import "ecosystemServices" plus haut).
const formatEcosystemServices = () =>
  ecosystemServices
    .map((service) => {

      const emoji =
        ECOSYSTEM_SERVICE_EMOJI[service.key] || "•"

      return `${emoji} ${service.title} — ${service.text}`

    })
    .join("\n")


/* =========================================================
   CONTENU LIVE DES AUTRES PAGES PRODUITS
   Mêmes tableaux que ceux affichés sur Metua'a Eats / Mappers /
   Entreprise / Chauffeur / Programme M+ (voir imports en haut de
   fichier) — Combi les lit directement, il ne les recopie jamais.
========================================================= */

const CHATBOT_MESSAGE_CATALOG = {
  fr: frMessages,
  en: enMessages
}

// Résout un identifiant de traduction react-intl (ex.
// "enterprise.feature1.title") vers le texte réel de la locale
// demandée. Renvoie l'identifiant tel quel si la clé est introuvable
// (même repli que react-intl) plutôt que de planter.
const resolveMessage = (id, locale) => {

  if (!id) return ""

  const catalog =
    CHATBOT_MESSAGE_CATALOG[locale] || CHATBOT_MESSAGE_CATALOG.fr

  return catalog[id] || id

}

// Pour les pages dont les cartes contiennent déjà du texte en clair
// (title/text), pas d'identifiant à résoudre.
const formatFeatureList = (cards) =>
  cards
    .map((card) => `• ${card.title} — ${card.text}`)
    .join("\n")

// Pour les pages dont les cartes stockent des identifiants de
// traduction (titleId/textId) plutôt que du texte en clair.
const formatFeatureListFromIds = (cards, locale) =>
  cards
    .map((card) =>
      `• ${resolveMessage(card.titleId, locale)} — ${resolveMessage(card.textId, locale)}`
    )
    .join("\n")

// Les 5 vraies étapes "devenir chauffeur" affichées sur la page
// Metua'a Chauffeur (driverSteps), numérotées.
const formatDriverSteps = (locale) =>
  driverSteps
    .map((step, index) =>
      `${index + 1}. ${resolveMessage(step.titleId, locale)} — ${resolveMessage(step.descriptionId, locale)}`
    )
    .join("\n")

// Résumé + points clés de l'étape 1 du Programme M+ (première chose
// affichée sur la page — sert de présentation générale du programme).
const formatMplusIntro = (locale) => {

  const step1 =
    mplusStepIntro[1]

  if (!step1) return ""

  const summary =
    resolveMessage(step1.summaryKey, locale)

  const items =
    (step1.items || [])
      .map((item) =>
        `• ${resolveMessage(item.titleKey, locale)} — ${resolveMessage(item.textKey, locale)}`
      )
      .join("\n")

  return [summary, items].filter(Boolean).join("\n\n")

}


/* =========================================================
   DONNÉES LIVE DU COMPTE CONNECTÉ (solde + profil)
   Mêmes services REST déjà utilisés par WalletClient/ProfileClient,
   avec le token/id déjà disponibles côté AppContent. On renvoie null
   en cas d'erreur réseau (pour distinguer "erreur" de "rien à
   afficher") — même convention que dans web-lewootrack.
========================================================= */

const fetchClientWallets = async (token) => {

  try {

    const res =
      await WalletApi.getClientWallets(token)

    return res?.data?.data || []

  } catch (err) {

    return null

  }

}

const fetchUserProfile = async (userid) => {

  try {

    const res =
      await ProfileApi.getUserDetailsById(userid)

    return res?.data?.data || res?.data || null

  } catch (err) {

    return null

  }

}

const formatWalletAmount = (amount) => {

  const num =
    parseFloat(amount) || 0

  return num.toLocaleString("fr-FR")

}

const formatWalletBalances = (wallets, locale) =>
  wallets
    .map((wallet) => {

      const amount =
        formatWalletAmount(wallet.balance)

      const currency =
        wallet.currency || "FCFA"

      const primaryTag =
        wallet.is_primary
          ? (locale === "fr" ? " (principal)" : " (primary)")
          : ""

      return `💰 ${amount} ${currency}${primaryTag}`

    })
    .join("\n")

const formatProfileDetails = (user) => {

  const name =
    user.name ||
    [user.first_name || user.firstName, user.last_name || user.lastName]
      .filter(Boolean)
      .join(" ") ||
    null

  const email =
    user.email || null

  const phone =
    user.phone || user.mobile_number || user.mobileNumber || null

  const lines = []

  if (name) lines.push(`👤 ${name}`)
  if (email) lines.push(`📧 ${email}`)
  if (phone) lines.push(`📱 ${phone}`)

  return lines.length ? lines.join("\n") : null

}


/* =========================================================
   FAQ VOYAGES
========================================================= */

const buildTravelFaqBlocks = (t, locale) => {

  const blocks = {}

  const categories =
    TRAVEL_FAQ_CATEGORIES[locale] ||
    TRAVEL_FAQ_CATEGORIES.fr

  const faqByCategory =
    TRAVEL_FAQ[locale] ||
    TRAVEL_FAQ.fr


  /* -----------------------------------------
     MENU VOYAGE
  ----------------------------------------- */

  blocks.voyages = {

    message: t.voyagesIntro,

    options: [
      ...categories,
      t.menu.retour,
      t.menu.autre
    ],

    path: (params) => {

      if (
        params.userInput ===
        t.menu.retour
      ) {
        return "start"
      }

      if (
        params.userInput ===
        t.menu.autre
      ) {
        return "question"
      }

      const category =
        categories.find(
          (category) =>
            category === params.userInput
        )

      if (category) {

        return `voyages_cat_${categories.indexOf(category)}`

      }

      return resolveFreeTextBlock(params.userInput, locale)

    }

  }


  /* -----------------------------------------
     CATÉGORIES
  ----------------------------------------- */

  categories.forEach(
    (category, categoryIndex) => {

      const questions =
        faqByCategory[category]

      const categoryBlock =
        `voyages_cat_${categoryIndex}`


      blocks[categoryBlock] = {

        message:
          locale === "en"
            ? `Sure! 👇 Here are the questions about ${category.toLowerCase()}.`
            : `Bien sûr mon combi ! 👇 Voici les questions sur ${category.toLowerCase()}.`,

        options: [

          ...questions.map(
            (question) =>
              question.question
          ),

          t.menu.retourCategories,

          t.menu.retour,

          t.menu.autre

        ],

        path: (params) => {

          if (
            params.userInput ===
            t.menu.retourCategories
          ) {
            return "voyages"
          }

          if (
            params.userInput ===
            t.menu.retour
          ) {
            return "start"
          }

          if (
            params.userInput ===
            t.menu.autre
          ) {
            return "question"
          }

          const questionIndex =
            questions.findIndex(
              (question) =>
                question.question ===
                params.userInput
            )

          if (questionIndex !== -1) {

            return `voyages_q_${categoryIndex}_${questionIndex}`

          }

          return resolveFreeTextBlock(params.userInput, locale)

        }

      }


      /* -----------------------------------------
         RÉPONSES
      ----------------------------------------- */

      questions.forEach(
        (question, questionIndex) => {

          blocks[
            `voyages_q_${categoryIndex}_${questionIndex}`
          ] = {

            message:
              question.answer,

            options: [

              t.menu.retourCategories,

              t.menu.retour,

              t.menu.autre

            ],

            path: (params) => {

              if (
                params.userInput ===
                t.menu.retourCategories
              ) {
                return categoryBlock
              }

              if (
                params.userInput ===
                t.menu.autre
              ) {
                return "question"
              }

              if (
                params.userInput ===
                t.menu.retour
              ) {
                return "start"
              }

              return resolveFreeTextBlock(params.userInput, locale)

            }

          }

        }
      )

    }
  )


  return blocks

}


/* =========================================================
   RUBRIQUES DE L'ESPACE CLIENT CONNECTÉ
   Vraie sidebar du dashboard (Layouts/DashboardClientLayout, voir
   import en haut de fichier) — Combi décrit les rubriques réellement
   affichées au client connecté, jamais une rubrique commentée/inactive.
========================================================= */

const DASHBOARD_FEATURE_META = {
  "/dashboard-client": {
    key: "espace_dashboard",
    emoji: "🏠",
    fr: "Vue d'ensemble de ton activité : trajets récents, solde, raccourcis vers les autres rubriques.",
    en: "An overview of your activity: recent rides, balance, and shortcuts to the other sections."
  },
  "/dispatcher-passenger": {
    key: "espace_rides",
    emoji: "🚕",
    fr: "Demande une nouvelle course en direct : choisis ton point de départ, ta destination et un chauffeur disponible.",
    en: "Request a new ride live: choose your pickup point, destination, and an available driver."
  },
  "/trip-list-passenger": {
    key: "espace_myrides",
    emoji: "📋",
    fr: "La liste de toutes tes courses, en cours et passées.",
    en: "The list of all your rides, ongoing and past."
  },
  "/trajet-client": {
    key: "espace_bookings",
    emoji: "🗓️",
    fr: "Tes réservations de trajets interurbains (Metua'a Travel Hub) : à venir et passées.",
    en: "Your intercity ride bookings (Metua'a Travel Hub): upcoming and past."
  },
  "/wallet-client": {
    key: "espace_wallet",
    emoji: "💰",
    fr: "Le solde de ton portefeuille Metua'a, avec les options pour l'alimenter ou le synchroniser.",
    en: "Your Metua'a wallet balance, with options to top it up or sync it."
  },
  "/paiements-client": {
    key: "espace_transactions",
    emoji: "💳",
    fr: "L'historique de tes transactions et moyens de paiement enregistrés.",
    en: "Your transaction history and saved payment methods."
  },
  "/favoris-client": {
    key: "espace_favoris",
    emoji: "❤️",
    fr: "Tes adresses et trajets favoris, pour réserver plus vite la prochaine fois.",
    en: "Your favorite addresses and rides, so you can book faster next time."
  },
  "/profile-client": {
    key: "espace_profil",
    emoji: "👤",
    fr: "Tes informations de profil : nom, coordonnées, photo.",
    en: "Your profile information: name, contact details, photo."
  },
  "/settings-client": {
    key: "espace_parametres",
    emoji: "⚙️",
    fr: "Les réglages de ton compte : langue, notifications, préférences.",
    en: "Your account settings: language, notifications, preferences."
  }
}

// Ne garde que les rubriques réellement affichées dans la sidebar
// (sidebarPrimary + sidebarAccount) ET connues de DASHBOARD_FEATURE_META
// — si une rubrique de la sidebar est commentée/retirée côté page, ou
// si son href n'a pas (encore) de description ici, Combi ne la
// mentionne pas.
const listDashboardFeatures = () =>
  [...clientSidebarPrimary, ...clientSidebarAccount]
    .map((item) => ({ ...item, meta: DASHBOARD_FEATURE_META[item.href] }))
    .filter((item) => item.meta)

const formatDashboardFeatures = (locale) =>
  listDashboardFeatures()
    .map(
      (item) =>
        `${item.meta.emoji} ${resolveMessage(item.messageId, locale)} — ${item.meta[locale] || item.meta.fr}`
    )
    .join("\n")

const buildDashboardFeatureBlocks = (t, locale) => {

  const blocks = {}

  const features = listDashboardFeatures()

  // buildDashboardFeatureBlocks est en dehors de buildFlow (comme
  // buildTravelFaqBlocks) : pas d'accès à routeAnswerBlockInput, on
  // réimplémente le même routage retour/autre/texte libre en local.
  const routeBack = (userInput) => {
    if (userInput === t.menu.retour) return "start"
    if (userInput === t.menu.autre) return "question"
    return resolveFreeTextBlock(userInput, locale)
  }

  blocks.espace_client = {

    message: `${t.reponses.espaceClientIntro}\n\n${formatDashboardFeatures(locale)}`,

    options: [
      ...features.map((item) => resolveMessage(item.messageId, locale)),
      t.menu.retour,
      t.menu.autre
    ],

    path: (params) => {

      const match = features.find(
        (item) => resolveMessage(item.messageId, locale) === params.userInput
      )

      if (match) {
        return match.meta.key
      }

      return routeBack(params.userInput)

    }

  }

  features.forEach((item) => {

    blocks[item.meta.key] = {

      message: `${item.meta.emoji} ${resolveMessage(item.messageId, locale)}\n\n${item.meta[locale] || item.meta.fr}\n\n${t.reponses.espaceClientNavHint}`,

      options: [t.menu.retour, t.menu.autre],

      path: (params) => routeBack(params.userInput)

    }

  })

  return blocks

}


/* =========================================================
   BOUTON "ÉCOUTER CE MESSAGE" (repris de web-lewootrack)

   Le réglage natif "audio" de react-chatbotify n'a qu'un interrupteur
   global dans le header (une fois activé, il lit ensuite TOUS les
   messages automatiquement) — remplacé ici par un vrai bouton 🔈,
   affiché sous CHAQUE réponse de Combi via l'attribut "component" des
   blocs (fonctionnalité native de react-chatbotify), qui ne lit QUE ce
   message précis, uniquement au clic. Voir buildSettings plus bas :
   le bouton audio global du header est retiré, settings.audio.disabled
   passe à true.
========================================================= */

const CombiSpeakButton = ({ locale, getText }) => {

  const [speaking, setSpeaking] =
    useState(false)

  const utteranceRef =
    useRef(null)

  const handleClick = (event) => {

    event.stopPropagation()

    if (typeof window === "undefined" || !("speechSynthesis" in window)) {
      return
    }

    // Un clic sur un bouton déjà en train de lire arrête cette lecture.
    if (speaking) {
      window.speechSynthesis.cancel()
      setSpeaking(false)
      return
    }

    const text =
      (typeof getText === "function" ? getText() : "") || ""

    const trimmed =
      text.trim()

    if (!trimmed) {
      return
    }

    // Coupe toute autre lecture en cours avant de démarrer celle-ci.
    window.speechSynthesis.cancel()

    const utterance =
      new SpeechSynthesisUtterance(trimmed)

    utterance.lang =
      locale === "en" ? "en-US" : "fr-FR"

    utterance.rate = 1

    utterance.onend = () => setSpeaking(false)
    utterance.onerror = () => setSpeaking(false)

    // Garde une référence tant que ça parle : certains navigateurs
    // (Chrome) coupent silencieusement le son si cet objet est
    // nettoyé par le garbage collector avant/pendant la lecture.
    utteranceRef.current = utterance

    window.speechSynthesis.speak(utterance)
    setSpeaking(true)

  }

  // Coupe la lecture si CE bouton précis disparaît du DOM en pleine
  // lecture (ex : le composant est démonté).
  useEffect(() => {

    return () => {
      if (speaking && typeof window !== "undefined" && "speechSynthesis" in window) {
        window.speechSynthesis.cancel()
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (

    <div style={{ display: "block", width: "100%", clear: "both", marginTop: "4px" }}>

      <button
        type="button"
        onClick={handleClick}
        aria-label={locale === "en" ? "Listen to this message" : "Écouter ce message"}
        title={locale === "en" ? "Listen to this message" : "Écouter ce message"}
        style={{
          display: "inline-flex",
          alignItems: "center",
          background: "transparent",
          border: "none",
          cursor: "pointer",
          fontSize: "14px",
          lineHeight: 1,
          opacity: 0.75,
          padding: "2px 4px"
        }}
      >
        {speaking ? "🔊" : "🔈"}
      </button>

    </div>

  )

}

// Texte à lire pour chaque bloc, tenu à jour au fil de la conversation
// (clé = nom du bloc). Rempli automatiquement par attachSpeakButtons :
// - texte statique -> connu et stocké immédiatement.
// - texte dynamique (message asynchrone : compte, FAQ...) -> stocké dès
//   que la fonction "message" du bloc a fini de le calculer.
// Lu par CombiSpeakButton au clic, via la fonction "getText" de chaque
// bouton (une fermeture par bloc, donc chaque bouton lit SON bloc).
const dynamicSpeakTexts = {}

// Ajoute automatiquement le bouton "écouter" à tous les blocs qui ont
// un message (donc quasi tous) — un seul endroit à maintenir plutôt que
// d'ajouter "component:" bloc par bloc. Enveloppe aussi la fonction
// "message" (si c'en est une) pour capturer automatiquement le texte
// réellement affiché, sans toucher au code de chaque bloc.
const attachSpeakButtons = (blocks, locale) => {

  Object.keys(blocks).forEach((key) => {

    const block = blocks[key]

    if (!block || block.message === undefined || block.component !== undefined) {
      return
    }

    if (typeof block.message === "function") {

      const originalMessage = block.message

      block.message = async (params) => {
        const text = await originalMessage(params)
        dynamicSpeakTexts[key] = text
        return text
      }

    } else {

      // Message statique : le texte est déjà connu maintenant, pas
      // besoin d'attendre que le bloc soit affiché.
      dynamicSpeakTexts[key] = block.message

    }

    block.component = (
      <CombiSpeakButton
        locale={locale}
        getText={() => dynamicSpeakTexts[key]}
      />
    )

  })

  return blocks

}


/* =========================================================
   FLOW PRINCIPAL
========================================================= */

const buildFlow = (
  locale,
  displayName,
  isAuthenticated,
  authToken,
  userid,
  lewooPayToken
) => {

  const t =
    CHATBOT_TEXT[locale]

  const greetingMessage =
    displayName
      ? pickGreeting(
          locale,
          displayName
        )
      : t.greetingGuest


  // Les intitulés services/chauffeur/reserver sont reformulés côté
  // connecté (ton plus direct/actionnable) vs côté visiteur (ton plus
  // "comment ça marche ?") — la réponse pointée reste la même (voir
  // routeMainInput plus bas), seule la question proposée dans le menu
  // change selon isAuthenticated.
  const menuOptions = [

    isAuthenticated
      ? t.menu.servicesConnecte
      : t.menu.services,

    isAuthenticated
      ? t.menu.chauffeurConnecte
      : t.menu.chauffeur,

    isAuthenticated
      ? t.menu.reserverConnecte
      : t.menu.reserver

  ]


  menuOptions.push(
    t.menu.produits
  )


  if (!isAuthenticated) {

    menuOptions.push(
      t.menu.inscription
    )

  }


  if (isAuthenticated) {

    menuOptions.push(
      t.menu.compte
    )

    menuOptions.push(
      t.menu.espaceClient
    )

  }


  menuOptions.push(
    t.menu.voyages
  )

  menuOptions.push(
    t.menu.autre
  )


  // Routage partagé par tous les blocs qui affichent le menu
  // principal : clic sur une option connue, sinon texte libre
  // (salutation / petite conversation / FAQ, voir resolveFreeTextBlock).
  const routeMainInput = (userInput) => {

    if (userInput === t.menu.services || userInput === t.menu.servicesConnecte) {
      return "services"
    }

    if (userInput === t.menu.chauffeur || userInput === t.menu.chauffeurConnecte) {
      return "chauffeur"
    }

    if (userInput === t.menu.reserver || userInput === t.menu.reserverConnecte) {
      return "reserver"
    }

    if (userInput === t.menu.produits) {
      return "produits"
    }

    if (userInput === t.menu.inscription) {
      return "inscription"
    }

    if (userInput === t.menu.compte) {
      return "compte"
    }

    if (userInput === t.menu.espaceClient) {
      return "espace_client"
    }

    if (userInput === t.menu.voyages) {
      return "voyages"
    }

    if (userInput === t.menu.autre) {
      return "question"
    }

    return resolveFreeTextBlock(userInput, locale)

  }


  // Routage pour les blocs "réponse unique" (services, chauffeur,
  // reserver, inscription, faq_answer...) qui n'affichent que deux
  // options [Retour au menu / J'ai une autre question]. Avant, tout
  // texte libre qui ne matchait pas exactement "J'ai une autre
  // question" retombait bêtement sur "start" — ce qui empêchait de
  // reconnaître une salutation/un "merci"/"comment tu vas ?" tapé
  // depuis un de ces écrans plutôt que cliqué.
  const routeAnswerBlockInput = (userInput) => {

    if (userInput === t.menu.retour) {
      return "start"
    }

    if (userInput === t.menu.autre) {
      return "question"
    }

    return routeMainInput(userInput)

  }


  const blocks = {

    /* =====================================
       ACCUEIL
    ===================================== */

    start: {

      message:
        greetingMessage,

      options:
        menuOptions,

      path: (params) =>
        routeMainInput(params.userInput)

    },


    /* =====================================
       PETITE CONVERSATION
    ===================================== */

    // Réponse quand l'utilisateur envoie juste "salut"/"hey"/"bonsoir"...
    // plutôt qu'une vraie question ou un clic sur le menu.
    greeting_reply: {

      message:
        pickRandom(t.greetingReply),

      options:
        menuOptions,

      path: (params) =>
        routeMainInput(params.userInput)

    },

    // "Comment tu vas ?" — Combi répond et relance la question.
    how_are_you_reply: {

      message:
        pickRandom(t.howAreYouReply),

      path: (params) => {

        // Si l'utilisateur répond positivement à "et toi ?", Combi
        // enchaîne naturellement plutôt que de traiter ça comme une
        // question.
        if (isPositiveFeelingReply(params.userInput)) {
          return "how_are_you_continue"
        }

        return resolveFreeTextBlock(params.userInput, locale)

      }

    },

    // Suite naturelle après que l'utilisateur a répondu "ça va bien merci"
    how_are_you_continue: {

      message:
        pickRandom(t.howAreYouContinue(displayName)),

      options:
        menuOptions,

      path: (params) =>
        routeMainInput(params.userInput)

    },

    // "Qui es-tu ?" / "tu es qui ?"
    identity_reply: {

      message:
        pickRandom(t.identityReply),

      options:
        menuOptions,

      path: (params) =>
        routeMainInput(params.userInput)

    },

    // "Merci" / "merci c'est gentil" / "s'il te plaît"
    thanks_reply: {

      message:
        pickRandom(t.thanksReply),

      options:
        menuOptions,

      path: (params) =>
        routeMainInput(params.userInput)

    },


    /* =====================================
       SERVICES
    ===================================== */

    services: {

      // Le tableau "ecosystemServices" (vrai contenu de la page
      // Metua'a Travel Hub) n'existe qu'en français pour l'instant —
      // pas encore traduit via react-intl. On l'utilise donc pour le
      // français ; l'anglais garde son texte traduit à la main
      // (t.reponses.services) tant que la page source n'est pas
      // bilingue elle-même.
      message:
        withCta(
          locale === "fr"
            ? `${t.reponses.services}\n\n${formatEcosystemServices()}`
            : t.reponses.services,
          locale,
          isAuthenticated
        ),

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },


    /* =====================================
       CHAUFFEUR
    ===================================== */

    // Le déroulé en 3 points était écrit à la main ; on le remplace
    // par les 5 VRAIES étapes de la page "Metua'a Chauffeur"
    // (driverSteps, voir import en haut de fichier) pour que ça reste
    // à jour si le parcours d'inscription chauffeur change côté page.
    chauffeur: {

      message:
        `${t.reponses.chauffeur}\n\n${formatDriverSteps(locale)}\n\n${t.reponses.chauffeurOutro}`,

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },


    /* =====================================
       RÉSERVATION
    ===================================== */

    reserver: {

      message:
        withCta(
          t.reponses.reserver,
          locale,
          isAuthenticated
        ),

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },


    /* =====================================
       DÉCOUVRIR NOS PRODUITS
       Sous-menu vers les réponses dédiées à chaque produit Metua'a,
       chacune construite à partir du VRAI contenu de sa page (voir
       les imports/formatters en haut de fichier) — accessibles aussi
       en texte libre sans passer par ce sous-menu (voir
       MENU_TOPIC_KEYWORDS).
    ===================================== */

    produits: {

      message:
        t.reponses.produitsIntro,

      options: [

        t.menu.produitEats,

        t.menu.produitMappers,

        t.menu.produitEntreprise,

        t.menu.produitMplus,

        t.menu.produitTravelHub,

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) => {

        if (params.userInput === t.menu.produitEats) {
          return "produit_eats"
        }

        if (params.userInput === t.menu.produitMappers) {
          return "produit_mappers"
        }

        if (params.userInput === t.menu.produitEntreprise) {
          return "produit_entreprise"
        }

        if (params.userInput === t.menu.produitMplus) {
          return "produit_mplus"
        }

        if (params.userInput === t.menu.produitTravelHub) {
          return "produit_travelhub"
        }

        return routeAnswerBlockInput(params.userInput)

      }

    },

    // Metua'a Eats — featureCards de la page n'existe qu'en français
    // pour l'instant (même situation que "services", voir plus haut) :
    // français = vrai contenu live, anglais = équivalent écrit à la
    // main tant que la page source n'est pas bilingue.
    produit_eats: {

      message:
        withCta(
          locale === "fr"
            ? `${t.reponses.produitEatsIntro}\n\n${formatFeatureList(eatsFeatureCards)}`
            : t.reponses.produitEatsEn,
          locale,
          isAuthenticated
        ),

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },

    // Metua'a Mappers — même situation que Eats (featureCards fr uniquement).
    produit_mappers: {

      message:
        withCta(
          locale === "fr"
            ? `${t.reponses.produitMappersIntro}\n\n${formatFeatureList(mappersFeatureCards)}`
            : t.reponses.produitMappersEn,
          locale,
          isAuthenticated
        ),

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },

    // Metua'a Travel Hub — même situation (featureCards fr uniquement).
    produit_travelhub: {

      message:
        withCta(
          locale === "fr"
            ? `${t.reponses.produitTravelHubIntro}\n\n${formatFeatureList(travelHubFeatureCards)}`
            : t.reponses.produitTravelHubEn,
          locale,
          isAuthenticated
        ),

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },

    // Metua'a Entreprise — enterpriseFeatureCards est déjà bilingue
    // (titleId/textId réels), donc live dans les deux langues.
    produit_entreprise: {

      message:
        withCta(
          `${t.reponses.produitEntrepriseIntro}\n\n${formatFeatureListFromIds(enterpriseFeatureCards, locale)}`,
          locale,
          isAuthenticated
        ),

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },

    // Programme M+ — stepIntroEditorialContent est déjà bilingue, donc
    // live dans les deux langues.
    produit_mplus: {

      message:
        withCta(
          `${t.reponses.produitMplusIntro}\n\n${formatMplusIntro(locale)}`,
          locale,
          isAuthenticated
        ),

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },


    /* =====================================
       CRÉATION DU COMPTE
    ===================================== */

    inscription: {

      message:
        t.reponses.inscription,

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },


    /* =====================================
       MON COMPTE (données live, connecté uniquement)
    ===================================== */

    // Petit sous-menu, accessible uniquement quand isAuthenticated
    // (voir menuOptions plus haut) — mais aussi atteignable en texte
    // libre ("mon solde", "mon profil"...) même sans être connecté,
    // auquel cas compte_solde/compte_profil répondent proprement avec
    // une invitation à se connecter.
    compte: {

      message:
        t.compteIntro,

      options: [

        t.menu.compteSolde,

        t.menu.compteProfil,

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) => {

        if (params.userInput === t.menu.compteSolde) {
          return "compte_solde"
        }

        if (params.userInput === t.menu.compteProfil) {
          return "compte_profil"
        }

        return routeAnswerBlockInput(params.userInput)

      }

    },

    // Solde réel du/des portefeuille(s) du client connecté, via le
    // même service REST (WalletApi.getClientWallets) que la page
    // Portefeuille — avec le MÊME jeton qu'elle (lewooPayToken), pas
    // le jeton de connexion classique : le solde du portefeuille est
    // géré par un système d'auth séparé (LewooPay), voir
    // Pages/WalletClient. Repli sur un message générique si pas
    // connecté, sur "aucun portefeuille" si le compte n'a pas encore
    // de portefeuille LewooPay lié, et sur "erreur" seulement si le
    // fetch échoue vraiment — jamais d'erreur technique montrée à
    // l'utilisateur.
    compte_solde: {

      message: async (params) => {

        if (!isAuthenticated) {
          return t.compteConnexionRequise
        }

        if (!lewooPayToken) {
          return t.compteSoldeAucun
        }

        const wallets =
          await fetchClientWallets(lewooPayToken)

        if (wallets === null) {
          return t.compteErreur
        }

        if (!wallets.length) {
          return t.compteSoldeAucun
        }

        return `${t.compteSoldeIntro}\n\n${formatWalletBalances(wallets, locale)}`

      },

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },

    // Infos de profil réelles du client connecté, via le même service
    // REST (ProfileApi.getUserDetailsById) que la page Profil. Même
    // logique de repli que compte_solde.
    compte_profil: {

      message: async (params) => {

        if (!isAuthenticated || !userid) {
          return t.compteConnexionRequise
        }

        const profile =
          await fetchUserProfile(userid)

        if (profile === null) {
          return t.compteErreur
        }

        const formatted =
          formatProfileDetails(profile)

        if (!formatted) {
          return t.compteProfilVide
        }

        return `${t.compteProfilIntro}\n\n${formatted}`

      },

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },


    /* =====================================
       QUESTION LIBRE
    ===================================== */

    question: {

      message:
        t.autreQuestion,

      path: (params) => {

        // On vérifie d'abord si ce texte libre est en fait une
        // salutation / "comment tu vas ?" / "merci"... plutôt qu'une
        // vraie question à chercher dans la FAQ.
        const routed =
          resolveFreeTextBlock(
            params.userInput,
            locale
          )

        if (routed !== "question") {
          return routed
        }

        const faq =
          findFaqAnswer(
            params.userInput,
            locale
          )


        if (faq) {

          return "faq_answer"

        }


        return "faq_not_found"

      }

    },


    /* =====================================
       RÉPONSE FAQ
    ===================================== */

    faq_answer: {

      message: (params) => {

        const faq =
          findFaqAnswer(
            params.userInput,
            locale
          )

        return faq
          ? faq.answer
          : t.questionNonTrouvee

      },

      options: [

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) =>
        routeAnswerBlockInput(params.userInput)

    },


    /* =====================================
       QUESTION INCONNUE
    ===================================== */

    faq_not_found: {

      message:
        t.questionNonTrouvee,

      options: [

        t.menu.retry,

        t.menu.retour,

        t.menu.autre

      ],

      path: (params) => {

        if (
          params.userInput ===
          t.menu.retry
        ) {
          return "question"
        }

        return routeAnswerBlockInput(params.userInput)

      }

    }

  }


  /* Ajout des blocs FAQ (FR et EN) */

  const allBlocks = {
    ...blocks,
    ...buildTravelFaqBlocks(t, locale),
    ...buildDashboardFeatureBlocks(t, locale)
  }

  // Ajoute le bouton "écouter" sous chaque message juste avant de
  // renvoyer le flow complet (voir attachSpeakButtons plus haut).
  return attachSpeakButtons(allBlocks, locale)

}


/* =========================================================
   SETTINGS
========================================================= */

const buildSettings = (locale) => {

  const t =
    CHATBOT_TEXT[locale]


  return {

    general: {

      primaryColor:
        "#1b8a5a",

      secondaryColor:
        "#0f5c3a"

    },


    tooltip: {

      mode:
        "CLOSE",

      text:
        t.tooltip

    },


    chatButton: {

      icon:
        supportIcon

    },


    header: {

      title:
        t.headerTitle,

      showAvatar:
        true,

      avatar:
        metuaBotAvatarIdle,

      closeChatIcon:
        minimizeIcon,

      /* Plus de bouton audio global dans le header (qui lisait TOUT
         automatiquement une fois activé) : remplacé par un bouton
         "écouter" propre à chaque message, voir attachSpeakButtons /
         CombiSpeakButton plus haut — même choix que web-lewootrack. */

      buttons: [

        Button.CLOSE_CHAT_BUTTON

      ]

    },


    /* Evenements necessaires pour que CombiAvatarController
       puisse detecter les phases "reflexion" du bot */

    event: {

      rcbUserSubmitText:
        true,

      rcbPreProcessBlock:
        true,

      rcbPostProcessBlock:
        true

    },


    /* Desactive le son de notification par defaut de la lib :
       evite l'erreur "Unable to decode audio data" au chargement
       (le fichier .wav embarque par react-chatbotify ne se decode
       pas correctement dans certains environnements) */

    notification: {

      disabled:
        true

    },


    /* SAISIE VOCALE (micro -> texte)
       autoSendDisabled: true -> après la dictée, le texte reste dans
       le champ de saisie ; c'est l'utilisateur qui clique sur "Envoyer"
       pour confirmer, il n'y a plus d'envoi automatique tout seul
       (même réglage que web-lewootrack). */

    voice: {

      disabled:
        false,

      defaultToggledOn:
        false,

      language:
        locale === "en" ? "en-US" : "fr-FR",

      timeoutPeriod:
        10000,

      autoSendDisabled:
        true,

      sendAsAudio:
        false

    },


    /* LECTURE A VOIX HAUTE DES REPONSES DE COMBI
       Désactivé : la lecture ne se déclenche plus automatiquement pour
       TOUS les messages via l'interrupteur global. À la place, un
       bouton "écouter" est affiché sous CHAQUE message de Combi et ne
       lit QUE ce message précis, uniquement au clic (voir
       CombiSpeakButton plus haut — même choix que web-lewootrack). */

    audio: {

      disabled:
        true,

      defaultToggledOn:
        false,

      language:
        locale === "en" ? "en-US" : "fr-FR",

      rate:
        1,

      volume:
        1

    },


    footer: {

      text:
        t.footerText(),

      buttons:
        []

    },


    chatInput: {

      enabledPlaceholderText:
        t.inputPlaceholder,

      allowNewline:
        true,

      buttons: [

        Button.FILE_ATTACHMENT_BUTTON,

        Button.EMOJI_PICKER_BUTTON,

        Button.VOICE_MESSAGE_BUTTON,

        Button.SEND_MESSAGE_BUTTON

      ]

    },


    chatHistory: {

      viewChatHistoryButtonText:
        t.loadHistory

    }

  }

}


/* =========================================================
   STYLES
========================================================= */

const styles = {

  // Bulle d'info compacte (padding/police réduits par rapport aux
  // 16px / 20px par défaut de la librairie) — mêmes valeurs que
  // web-lewootrack.
  tooltipStyle: {

    padding:
      "10px 16px",

    fontSize:
      "15px"

  },


  headerStyle: {

    background:
      "linear-gradient(to right, #1b8a5a, #0f5c3a)"

  },


  chatButtonStyle: {

    backgroundColor:
      "#1b8a5a"

  },


  userBubbleStyle: {

    backgroundColor:
      "#1b8a5a"

  },


  botBubbleStyle: {

    backgroundColor:
      "#0f5c3a"

  },


  chatInputContainerStyle: {

    alignItems:
      "flex-end"

  },


  /* IMPORTANT :
     permet aux boutons FAQ d'être
     réellement interactifs */

  botOptionStyle: {

    cursor:
      "pointer",

    pointerEvents:
      "auto"

  },


  botOptionHoveredStyle: {

    cursor:
      "pointer",

    pointerEvents:
      "auto"

  },


  /* Rendre le bouton audio (header) plus discret,
     comme l'etait l'ancienne cloche de notification */

  audioButtonStyle: {

    width:
      "16px",

    height:
      "16px",

    opacity:
      0.75

  },


  audioButtonDisabledStyle: {

    width:
      "16px",

    height:
      "16px",

    opacity:
      0.45

  }

}


/* =========================================================
   APP CONTENT
========================================================= */

/* =========================================================
   FERMETURE DU CHAT PAR CLIC EXTERIEUR

   Aucun overlay n'est utilisé : les boutons FAQ du chatbot
   restent donc toujours cliquables.
========================================================= */

const ChatOutsideClick = ({ chatRootRef }) => {

  const {
    isChatWindowOpen,
    toggleChatWindow
  } = useChatWindow()


  useEffect(() => {

    if (!isChatWindowOpen) {
      return undefined
    }


    const closeOnOutsideClick = (event) => {

      const chatRoot =
        chatRootRef.current


      /*
         Tous les clics dans le chatbot sont ignores :
         boutons FAQ, champ de saisie, header et bouton fermer.
      */
      if (
        chatRoot &&
        chatRoot.contains(event.target)
      ) {
        return
      }


      /* Clic en dehors du chatbot : fermeture. */
      void toggleChatWindow(false)

    }


    /*
       Le mode capture permet de detecter le clic meme si une
       partie du site utilise stopPropagation().
    */
    document.addEventListener(
      "pointerdown",
      closeOnOutsideClick,
      true
    )


    return () => {

      document.removeEventListener(
        "pointerdown",
        closeOnOutsideClick,
        true
      )

    }

  }, [
    isChatWindowOpen,
    toggleChatWindow,
    chatRootRef
  ])


  return null

}


/* =========================================================
   AVATAR REACTIF DE COMBI

   Fait basculer l'avatar du header entre 3 etats selon
   ce qui se passe dans la conversation :

   - idle     : etat de repos (logo M statique)
   - wave     : salut anime, joue a l'ouverture du chat
   - thinking : bulle "..." animee, joue pendant que
                Combi traite la question / le clic

   Repose sur les hooks useChatWindow / useSettings et sur
   les evenements rcb-user-submit-text, rcb-pre-process-block
   et rcb-post-process-block (actives via settings.event).
========================================================= */

const COMBI_AVATARS = {

  idle:
    metuaBotAvatarIdle,

  wave:
    metuaBotAvatarWave,

  thinking:
    metuaBotAvatarThinking

}

const CombiAvatarController = ({ chatRootRef }) => {

  const { isChatWindowOpen } =
    useChatWindow()

  const { updateSettings } =
    useSettings()

  const modeRef =
    useRef("idle")

  const wasOpenRef =
    useRef(false)

  const waveTimeoutRef =
    useRef(null)

  const thinkingTimeoutRef =
    useRef(null)


  const setAvatarMode = useCallback((mode) => {

    if (modeRef.current === mode) {
      return
    }

    modeRef.current = mode

    updateSettings({

      header: {
        avatar: COMBI_AVATARS[mode]
      }

    })

  }, [updateSettings])


  /* -----------------------------------------
     SALUT A L'OUVERTURE DU CHAT
  ----------------------------------------- */

  useEffect(() => {

    const justOpened =
      isChatWindowOpen &&
      !wasOpenRef.current

    wasOpenRef.current =
      isChatWindowOpen


    if (!justOpened) {
      return undefined
    }


    setAvatarMode("wave")

    if (waveTimeoutRef.current) {
      clearTimeout(waveTimeoutRef.current)
    }

    waveTimeoutRef.current = setTimeout(() => {

      if (modeRef.current === "wave") {
        setAvatarMode("idle")
      }

    }, 2200)


    return () => {

      if (waveTimeoutRef.current) {
        clearTimeout(waveTimeoutRef.current)
      }

    }

  }, [isChatWindowOpen, setAvatarMode])


  /* -----------------------------------------
     REFLEXION PENDANT LE TRAITEMENT D'UN BLOC
  ----------------------------------------- */

  useEffect(() => {

    const startThinking = () => {

      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current)
        thinkingTimeoutRef.current = null
      }

      setAvatarMode("thinking")

    }


    const stopThinking = () => {

      /* petite duree minimale visible pour eviter
         un clignotement si la reponse est instantanee */

      thinkingTimeoutRef.current = setTimeout(() => {

        if (modeRef.current === "thinking") {

          setAvatarMode("idle")


          /* petit rebond de "reaction" quand la reponse
             vient d'etre livree */

          const root =
            chatRootRef?.current

          if (root) {

            root.classList.add("combi-reacting")

            setTimeout(() => {
              root.classList.remove("combi-reacting")
            }, 750)

          }

        }

      }, 400)

    }


    window.addEventListener(
      "rcb-user-submit-text",
      startThinking
    )

    window.addEventListener(
      "rcb-pre-process-block",
      startThinking
    )

    window.addEventListener(
      "rcb-post-process-block",
      stopThinking
    )


    return () => {

      window.removeEventListener(
        "rcb-user-submit-text",
        startThinking
      )

      window.removeEventListener(
        "rcb-pre-process-block",
        startThinking
      )

      window.removeEventListener(
        "rcb-post-process-block",
        stopThinking
      )

      if (thinkingTimeoutRef.current) {
        clearTimeout(thinkingTimeoutRef.current)
      }

    }

  }, [setAvatarMode, chatRootRef])


  return null

}

const AppContent = () => {

  const userType =
    useSelector(
      (state) =>
        state?.AuthReducer?.userType || ""
    )


  const isAuthenticated =
    useSelector(
      (state) =>
        state?.AuthReducer?.isAuthenticated ||
        false
    )


  const authUser =
    useSelector(
      (state) =>
        state?.AuthReducer?.user ||
        null
    )


  const userid =
    useSelector(
      (state) =>
        state?.AuthReducer?.userid ||
        ""
    )


  const authToken =
    useSelector(
      (state) =>
        state?.AuthReducer?.token ||
        ""
    )


  // Le solde du portefeuille ne passe PAS par le jeton de connexion
  // classique (authToken) mais par un jeton séparé propre au système
  // de paiement LewooPay — même source que la vraie page Portefeuille
  // (Pages/WalletClient), voir state.AuthReducer.lewooPayToken /
  // localStorage "lewooPayToken".
  const lewooPayToken =
    useSelector(
      (state) =>
        state?.AuthReducer?.lewooPayToken ||
        (typeof window !== "undefined" ? localStorage.getItem("lewooPayToken") : "") ||
        ""
    )


  const [zoomLevel] =
    useState(0.8)


  const chatRootRef =
    useRef(null)


  const { locale } =
    useLanguage()


  const displayName =
    isAuthenticated
      ? getDisplayName(authUser)
      : null


  /* =====================================
     FLOW SANS OPENAI
  ===================================== */

  const flow = useMemo(

    () =>
      buildFlow(
        locale,
        displayName,
        isAuthenticated,
        authToken,
        userid,
        lewooPayToken
      ),

    [
      locale,
      displayName,
      isAuthenticated,
      authToken,
      userid,
      lewooPayToken
    ]

  )


  const settings =
    useMemo(
      () =>
        buildSettings(locale),

      [locale]
    )


  return (

    <ThemeProvider
      theme={theme}
    >

      <ThemeProviderStyle
        theme={theme}
      >

        {/* Le zoom 80% du site (hors utilisateurs HOTEL) ne couvre
            volontairement QUE le reste du site ici — le chatbot est
            rendu en dehors (voir ChatBotProvider plus bas) pour
            s'afficher à sa vraie taille, comme dans web-lewootrack,
            au lieu d'être lui aussi rétréci à 80%. */}
        <div
          style={
            userType === "HOTEL"
              ? {}
              : {
                  zoom:
                    zoomLevel
                }
          }
        >

          <Loader />

          <SnackBar />

          <CssBaseline />

          <Router />

          <FloatingOrderButton
            to="/login"
          />

        </div>


        <ChatBotProvider>

          <div
            ref={chatRootRef}
          >

            <ChatBot

              key={
                `${locale}-${userid || "guest"}`
              }

              flow={flow}

              settings={settings}

              styles={styles}

            />

          </div>


          <ChatOutsideClick
            chatRootRef={chatRootRef}
          />

          <CombiAvatarController
            chatRootRef={chatRootRef}
          />

        </ChatBotProvider>

      </ThemeProviderStyle>

    </ThemeProvider>

  )

}


/* =========================================================
   APP
========================================================= */

const App = () => (

  <Provider
    store={Store}
  >

    <LanguageProvider>

      <AppContent />

    </LanguageProvider>

  </Provider>

)


export default App